import React, { useState } from 'react';
import { GALLERY_IMAGES, HERO_IMAGE } from '../data/mockData';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.05 });

  const openLightbox = (index) => setSelectedImage(index);
  const closeLightbox = () => setSelectedImage(null);
  const nextImg = () => setSelectedImage((prev) => (prev + 1) % GALLERY_IMAGES.length);
  const prevImg = () => setSelectedImage((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);

  return (
    <Layout>
      <PageHeader
        title="Life at"
        titleAccent="MISFiTS."
        subtitle="Real moments from the mountains — through the lens."
        bgImage={HERO_IMAGE}
      />

      <section className="pb-24 px-6 lg:px-8 bg-[#111111]">
        <div ref={ref} className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {GALLERY_IMAGES.map((img, i) => (
              <motion.div
                key={img.alt}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${i === 0 ? 'md:row-span-2 md:col-span-1' : ''}`}
                onClick={() => openLightbox(i)}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className={`w-full object-cover transition-transform duration-700 group-hover:scale-110 ${i === 0 ? 'h-full min-h-[250px] md:min-h-[400px]' : 'h-48 md:h-56'}`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-500" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm tracking-wide bg-black/40 backdrop-blur-sm px-4 py-2 rounded-full">View</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
            onClick={closeLightbox}
          >
            <button onClick={closeLightbox} className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors"><X size={28} /></button>
            <button onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-4 md:left-8 text-white/70 hover:text-white transition-colors"><ChevronLeft size={36} /></button>
            <motion.img
              key={selectedImage}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={GALLERY_IMAGES[selectedImage].src}
              alt={GALLERY_IMAGES[selectedImage].alt}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-4 md:right-8 text-white/70 hover:text-white transition-colors"><ChevronRight size={36} /></button>
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm">{selectedImage + 1} / {GALLERY_IMAGES.length}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default GalleryPage;
